import axios from "axios";
import { isNil, isEmpty } from "lodash";

/**
 * Retrieve sales data for a property.
 *
 * @param {number} propertyId - The ID of the property for which to retrieve sales data.
 *
 * @returns {Promise} A Promise that resolves with the sales data.
 */
export const getSalesData = async (propertyId) => {
    return (await axios.get(`/api/properties/${propertyId}/sales_data`)).data;
};

/**
 * Retrieves daily guest properties for a specific year and week.
 * @param {Object} options - Options object.
 * @param {number} options.year - The year for which to retrieve daily guest properties.
 * @param {number} options.week - The week for which to retrieve daily guest properties.
 * @returns {Promise<Object>} A promise that resolves with the daily guest properties data.
 */
export const getDailyGuestProperties = async ({ year, week }) => {
    const query = new URLSearchParams({ year, week }).toString();
    return (await axios.get(`/api/properties/daily-guest-properties?${query}`)).data;
};

/**
 * Asynchronously fetches a daily property data from the server based on the current date.
 *
 * @returns {Promise} A Promise that resolves a single property data based on todays play_date.
 */
export const getLiveVariantDailyProperty = async ({ variant_link = "", play_date = "" }) => {
    const query = new URLSearchParams({ variant_link, play_date }).toString();
    return await axios.get(`/api/properties/daily?${query}`);
};

export const getNearestProperty = async ({ location = "", limit = 0, guessedProperties = [] }) => {
    const query = new URLSearchParams({ location, limit, guessed_properties: guessedProperties }).toString();
    return await axios.get(`/api/properties/nearest?${query}`);
};

export const resolveLink = async ({ short_link = "", play_date = "" }) => {
    const query = new URLSearchParams({ short_link, play_date }).toString();
    return await axios.get(`/api/properties/resolve-link?${query}`);
};

export const getCoordinates = async (address) => {
    if (!address) throw new Error("Address is required.");
    const query = new URLSearchParams({ address }).toString();
    const response = await axios.get(`/api/properties/geocode?${query}`);
    return response.data.coordinates;
};

export const getPlayableProperties = async ({ min_play_date, max_play_date, page, limit } = {}) => {
    const query = new URLSearchParams({
        ...(min_play_date && { min_play_date }),
        ...(max_play_date && { max_play_date }),
        ...(page && { page }),
        ...(limit && { limit }),
    }).toString();
    return await axios.get(`/api/properties/playable${query ? `?${query}` : ""}`);
};

export const getListingFeedProperties = async ({
    page = "",
    limit = "",
    userId,
    feedType = "",
    userFeedPreferenceId = "",
}) => {
    const query = new URLSearchParams({
        page,
        limit,
        feedType,
        ...(!isNil(userId) && !isEmpty(userId) && { userId }),
        ...(!isNil(userFeedPreferenceId) && !isEmpty(userFeedPreferenceId) && { userFeedPreferenceId }),
    }).toString();
    return await axios.get(`/api/properties/listing-feed?${query}`);
};

export const getListingPageProperty = async ({ property_id }) => {
    return await axios.get(`/api/properties/${property_id}`);
};

export const getListingPropertyGuessStats = async ({ property_id }) => {
    return await axios.get(`/api/properties/${property_id}/social/guess-statistics`);
};

/**
 * Fetches a random archived property from the server.
 * @returns {Promise} A Promise that resolves to a random archived property.
 */
export const getRandomArchivedProperty = async () => {
    return await axios.get("/api/properties/archive/random");
};

export const getPropertyByLinkCode = async ({ property_link_code }) => {
    return await axios.get(`/api/properties/code/${property_link_code}`);
};

export const setDailyPlayableProperty = async ({ play_date, mls_id, image_index, url }) => {
    return await axios.post("/api/properties/playable", {
        ...(mls_id && { listing_id: mls_id }),
        ...(url && { url }),
        play_date,
        image_index: image_index || 1, // Default to 1
    });
};
